import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import Pagination from '@vlsergey/react-bootstrap-pagination';
import moment from 'moment';
import {
  Badge,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import { fetchPremiumCareList } from '../action/action';
import FilterSearchQueue from '../../../components/FilterSearch/FilterSearchQueue';
import PremiumCareDetailsOffcanvas from './PremiumCareDetailsOffcanvas';
import {
  PREMIUM_CARE_STATUS_OPTIONS,
  isEditable,
  statusColor,
  statusLabel,
} from '../constants';

const limitByValues = [10, 25, 50, 100];

/** Placeholder rows shown while the list loads. */
const SKELETON_ROWS = 5;

/**
 * Columns shown in the table. Kept minimal on purpose - everything else is
 * in the details drawer behind the View button.
 */
const COLUMNS = [
  { key: 'receiptNumber', label: 'Receipt / SO No' },
  { key: 'matchedItemCodes', label: 'SKU / Product' },
  { key: 'customerName', label: 'Customer' },
  { key: 'storeCode', label: 'Store' },
  { key: 'invoiceAmount', label: 'Amount' },
  { key: 'status', label: 'Status' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'createdAt', label: 'Created At' },
];

/**
 * Storefront product preview. The same live URL is used in dev and
 * production, so it is not driven by REACT_APP_WEB_URL.
 */
const PRODUCT_PREVIEW_URL = 'https://www.danubehome.com/ae/en/p/from-oms-preview-';

const productUrl = (sku) => `${PRODUCT_PREVIEW_URL}${sku}`;

/**
 * pdfUrl is not always a link - some records carry a bare document
 * reference (e.g. "000000WDE8000682967"), which must not become an anchor.
 */
/**
 * matchedItems ({ itemCode, productName }) is the newer shape; older records
 * only carry matchedItemCodes, so fall back to those.
 */
const matchedItemsOf = (row) => {
  if (Array.isArray(row?.matchedItems) && row.matchedItems.length > 0) {
    return row.matchedItems.map((item) => ({
      itemCode: item?.itemCode || '',
      productName: item?.productName || '',
    }));
  }

  return (Array.isArray(row?.matchedItemCodes) ? row.matchedItemCodes : []).map(
    (code) => ({ itemCode: code, productName: '' })
  );
};

const isUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

/**
 * The Troy listing envelope is not fixed, so find the first list-shaped node
 * instead of assuming one key.
 */
const extractRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.data?.items,
    payload?.items,
    payload?.data?.data?.items,
    payload?.data?.list,
    payload?.data,
  ];

  return candidates.find((node) => Array.isArray(node)) || [];
};

const extractTotal = (payload, rowCount) => {
  const candidates = [
    payload?.data?.total,
    payload?.total,
    payload?.data?.data?.total,
  ];
  const total = candidates.find(
    (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
  );

  return Number.isFinite(Number(total)) ? Number(total) : rowCount;
};

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const renderCell = (row, key) => {
  const value = row?.[key];

  if (key === 'receiptNumber') {
    const receipt = row?.receiptNumber;
    const salesOrder = row?.salesOrderNo;

    return (
      <div>
        <span className="d-block">{formatCell(receipt)}</span>
        {/* Often identical - only worth a second line when they differ. */}
        {salesOrder && salesOrder !== receipt && (
          <span className="d-block text-muted fs-12">{salesOrder}</span>
        )}
      </div>
    );
  }

  if (key === 'invoiceAmount') {
    // Older records predate this field, so absent is '-', not 0.
    if (value === null || value === undefined || value === '') return '-';
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';

    return `${row?.currency || 'AED'} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  if (key === 'remarks') {
    if (!value) return '-';

    // Remarks can run to a full sentence; clamp to two lines and keep the
    // full text on hover. The drawer shows it in full.
    return (
      <span
        title={value}
        className="fs-12"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          maxWidth: 240,
          whiteSpace: 'normal',
        }}
      >
        {value}
      </span>
    );
  }

  if (key === 'customerName') {
    return (
      <div>
        <span className="d-block">{formatCell(row?.customerName)}</span>
        {row?.customerMobile && (
          <span className="d-block text-muted fs-12">{row.customerMobile}</span>
        )}
        {row?.customerEmail && (
          <span className="d-block text-muted fs-12">{row.customerEmail}</span>
        )}
      </div>
    );
  }

  if (key === 'matchedItemCodes') {
    const items = matchedItemsOf(row).filter((item) => item.itemCode);
    if (items.length === 0) return '-';

    return (
      <div className="d-flex flex-column gap-2">
        {items.map((item, index) => (
          <div key={`${item.itemCode}-${index}`}>
            <a
              href={productUrl(item.itemCode)}
              target="_blank"
              rel="noopener noreferrer"
              title="Go to Product"
              className="d-inline-flex align-items-center gap-1"
            >
              {item.itemCode}
              <i className="bx bx-link-external fs-6"></i>
            </a>
            {item.productName && (
              <span className="d-block text-muted fs-12">
                {item.productName}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (key === 'status') {
    return (
      <Badge color={statusColor(value)} className="text-uppercase">
        {statusLabel(value)}
      </Badge>
    );
  }

  if (key === 'createdAt') {
    return value ? moment(value).format('DD MMM YYYY, hh:mm A') : '-';
  }

  return formatCell(value);
};

function PremiumCareList({ headerActions }) {
  const dispatch = useDispatch();

  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [searchFilter, setSearchFilter] = useUrlSearchParams({ search: '' });
  const [statusFilter, setStatusFilter] = useUrlSearchParams({ status: 'All' });
  const [advancedFilter, setAdvancedFilter] = useState({});
  const [limitOpen, setLimitOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [refreshList, setRefreshList] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const { premiumCareList, isLoading } = useSelector((state) => state.premiumCare);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchPremiumCareList({
        params: {
          page: Number(paginationValue.page) + 1,
          limit: Number(limitValue.limit),
          search: searchFilter?.search || '',
          status: statusFilter?.status === 'All' ? '' : statusFilter?.status,
          date_from: advancedFilter?.frmDt || '',
          date_to: advancedFilter?.toDt || '',
        },
        dispatch,
      });
    }, 300);

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationValue.page,
    limitValue.limit,
    searchFilter?.search,
    statusFilter?.status,
    advancedFilter,
    refreshList,
  ]);

  const rows = useMemo(() => extractRows(premiumCareList), [premiumCareList]);

  const totalRecords = extractTotal(premiumCareList, rows.length);
  const totalPages = Math.max(
    Math.ceil(totalRecords / Number(limitValue.limit || 10)),
    1
  );

  const handleLimit = (limit) => {
    setLimitValue({ limit: Number(limit) });
    setPaginationValue({ page: 0 });
  };

  return (
    <Row>
      <Col xl={12}>
        <FilterSearchQueue
          type={'premiumcare'}
          setAdvancedFilter={setAdvancedFilter}
          setSearchFilter={setSearchFilter}
          setStatusFilter={setStatusFilter}
          statusList={PREMIUM_CARE_STATUS_OPTIONS}
          headerActions={headerActions}
        />

        <div className="dt-table">
          {isLoading && <Spinner className="ms-2 sploader" color="primary" />}

          <div className="table-responsive">
            <Table className="OrderListTable table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  {COLUMNS.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th>Action</th>
                </tr>
              </thead>
              {isLoading ? (
                <tbody>
                  {[...Array(SKELETON_ROWS)].map((_, rowIndex) => (
                    <tr key={rowIndex}>
                      {[...Array(COLUMNS.length + 2)].map((__, cellIndex) => (
                        <td key={cellIndex}>
                          <p className="placeholder-glow">
                            <span className="placeholder col-8"></span>
                          </p>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length + 2} className="text-center py-4">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row?._id || row?.id || index}>
                        <td>
                          {Number(paginationValue.page) * Number(limitValue.limit) +
                            index +
                            1}
                        </td>
                        {COLUMNS.map((column) => (
                          <td key={column.key}>{renderCell(row, column.key)}</td>
                        ))}
                        <td nowrap="nowrap">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="btn btn-sm btn-outline-warning waves-effect waves-light"
                              title="View Premium Care Details"
                              onClick={() => {
                                setIsEdit(false);
                                setSelectedRecord(row);
                              }}
                            >
                              <i className="mdi mdi-eye-outline"></i>
                            </span>
                            {isEditable(row?.status) && (
                              <span
                                className="btn btn-sm btn-outline-primary waves-effect waves-light"
                                title="Update Status / Remarks"
                                onClick={() => {
                                  setIsEdit(true);
                                  setSelectedRecord(row);
                                }}
                              >
                                <i className="mdi mdi-pencil-outline"></i>
                              </span>
                            )}
                            {isUrl(row?.pdfUrl) && (
                              <a
                                className="btn btn-sm btn-outline-dark waves-effect waves-light"
                                href={row.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View / Download Invoice"
                              >
                                <i className="mdi mdi-file-pdf-box"></i>
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              )}
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Dropdown isOpen={limitOpen} toggle={() => setLimitOpen(!limitOpen)}>
              <DropdownToggle tag="button" className="btn btn-dark">
                {limitValue.limit} Items <i className="mdi mdi-chevron-down" />
              </DropdownToggle>
              <DropdownMenu>
                {limitByValues.map((limit) => (
                  <DropdownItem key={limit} href="#" onClick={() => handleLimit(limit)}>
                    {limit} Items
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <Pagination
              id="listPage"
              value={Number(paginationValue.page)}
              totalPages={totalPages}
              onChange={(e) => setPaginationValue({ page: e.target.value })}
            />
          </div>
        </div>
      </Col>

      <PremiumCareDetailsOffcanvas
        isOpen={!!selectedRecord}
        toggle={() => setSelectedRecord(null)}
        record={selectedRecord}
        isEdit={isEdit}
        onUpdated={() => setRefreshList((prev) => !prev)}
      />
    </Row>
  );
}

export default PremiumCareList;
